import Dexie from 'dexie'
import type {
  AddItemsRequest,
  BatchAssignRequest,
  BatchResult,
  RemoveItemsRequest,
} from '../api/contracts'
import { ApiError } from '../api/errors'
import * as palletApi from '../api/pallets'
import { db } from './db'
import { recordSuccessfulSync } from './operations'
import type { OutboxOp } from './types'

const DEFAULT_INITIAL_BACKOFF_MS = 1_000
const DEFAULT_MAX_BACKOFF_MS = 60_000

export interface SyncWorkerOptions {
  clock?: () => number
  initialBackoffMs?: number
  maxBackoffMs?: number
}

export interface SyncWorkerStartOptions {
  isOnline: boolean
  isAuthenticated: boolean
}

export interface SyncWorker {
  start(options: SyncWorkerStartOptions): void
  stop(): void
  setOnline(isOnline: boolean): void
  setAuthenticated(isAuthenticated: boolean): void
  wake(): void
  drain(): Promise<void>
}

/**
 * Drains one request per operation. The database row remains the source of truth for
 * recovery; in-memory sets only prevent this worker from dispatching the same pallet
 * twice during one drain pass.
 */
class OutboxSyncWorker implements SyncWorker {
  private readonly clock: () => number
  private readonly initialBackoffMs: number
  private readonly maxBackoffMs: number
  private started = false
  private isOnline = true
  private isAuthenticated = false
  private blockedBySession = false
  private drainPromise: Promise<void> | null = null
  private wakeScheduled = false
  private retryTimer: ReturnType<typeof setTimeout> | null = null

  private readonly onStorageMutated = () => this.wake()
  private readonly onOnline = () => this.setOnline(true)
  private readonly onOffline = () => this.setOnline(false)
  private readonly onVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      this.wake()
    }
  }

  constructor(options: SyncWorkerOptions = {}) {
    this.clock = options.clock ?? Date.now
    this.initialBackoffMs = options.initialBackoffMs ?? DEFAULT_INITIAL_BACKOFF_MS
    this.maxBackoffMs = options.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS
  }

  start(options: SyncWorkerStartOptions): void {
    this.isOnline = options.isOnline
    this.isAuthenticated = options.isAuthenticated

    if (!this.started) {
      this.started = true
      Dexie.on.storagemutated.subscribe(this.onStorageMutated)

      if (typeof window !== 'undefined') {
        window.addEventListener('online', this.onOnline)
        window.addEventListener('offline', this.onOffline)
      }

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this.onVisibilityChange)
      }
    }

    if (this.isAuthenticated) {
      this.blockedBySession = false
    }

    if (this.isAuthenticated && this.isOnline) {
      this.wake()
    } else {
      this.clearRetryTimer()
    }
  }

  stop(): void {
    if (!this.started) {
      return
    }

    this.started = false
    this.clearRetryTimer()
    Dexie.on.storagemutated.unsubscribe(this.onStorageMutated)

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline)
      window.removeEventListener('offline', this.onOffline)
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
  }

  setOnline(isOnline: boolean): void {
    this.isOnline = isOnline

    if (!isOnline) {
      this.clearRetryTimer()
      return
    }

    this.wake()
  }

  setAuthenticated(isAuthenticated: boolean): void {
    this.isAuthenticated = isAuthenticated

    if (!isAuthenticated) {
      this.clearRetryTimer()
      return
    }

    this.blockedBySession = false
    this.wake()
  }

  wake(): void {
    if (!this.canDrain() || this.wakeScheduled) {
      return
    }

    this.clearRetryTimer()
    this.wakeScheduled = true

    queueMicrotask(() => {
      this.wakeScheduled = false

      if (!this.canDrain()) {
        return
      }

      void this.drain().then(
        () => this.scheduleNextAttempt(),
        () => this.scheduleNextAttempt(),
      )
    })
  }

  drain(): Promise<void> {
    if (this.drainPromise !== null) {
      return this.drainPromise
    }

    this.blockedBySession = false
    this.drainPromise = this.runDrain().finally(() => {
      this.drainPromise = null
    })

    return this.drainPromise
  }

  private async runDrain(): Promise<void> {
    const activePallets = new Set<string>()

    while (!this.blockedBySession && (!this.started || this.canDrainDirectly())) {
      const candidates = await this.findReadyOps(activePallets)

      if (candidates.length === 0) {
        return
      }

      await Promise.all(candidates.map((op) => this.process(op, activePallets)))
    }
  }

  private async findReadyOps(activePallets: Set<string>): Promise<OutboxOp[]> {
    const now = this.clock()
    const ops = await db.outbox.orderBy('seq').toArray()
    const firstPendingPallets = new Set<string>()
    const candidates: OutboxOp[] = []

    for (const op of ops) {
      if (op.status !== 'queued' && op.status !== 'inflight') {
        continue
      }

      // The first non-terminal row owns the pallet until it gets a server response.
      if (firstPendingPallets.has(op.palletCode)) {
        continue
      }

      firstPendingPallets.add(op.palletCode)

      if (activePallets.has(op.palletCode)) {
        continue
      }

      if (op.status === 'queued' && op.nextAttemptAt > now) {
        continue
      }

      activePallets.add(op.palletCode)
      candidates.push(op)
    }

    return candidates
  }

  private async process(op: OutboxOp, activePallets: Set<string>): Promise<void> {
    try {
      // An inflight row is a crash-recovery row. Reasserting the status is harmless and
      // ensures a normal queued row is claimed before the request leaves the device.
      await db.outbox.update(op.id, { status: 'inflight' })

      let result: BatchResult
      try {
        result = await dispatch(op)
      } catch (error) {
        if (isSessionExpired(error)) {
          this.blockedBySession = true
          // The request body and identity remain untouched. Only the transient claim is
          // released so a re-login can wake the same operation.
          await db.outbox.update(op.id, { status: 'queued' })
          return
        }

        if (this.blockedBySession) {
          return
        }

        await this.backoff(op, error)
        return
      }

      // A different concurrent request may have reported session expiry first. Do not
      // reconcile this response after the worker has been stopped for that session.
      if (this.blockedBySession) {
        return
      }

      await reconcile(op, result, this.clock)
    } finally {
      activePallets.delete(op.palletCode)
    }
  }

  private async backoff(op: OutboxOp, error: unknown): Promise<void> {
    const attempts = op.attempts + 1
    const exponential = this.initialBackoffMs * 2 ** Math.min(attempts - 1, 30)
    const delay = Math.min(this.maxBackoffMs, exponential)

    await db.outbox.update(op.id, {
      status: 'queued',
      attempts,
      nextAttemptAt: this.clock() + delay,
      lastError: errorMessage(error),
    })
  }

  private async scheduleNextAttempt(): Promise<void> {
    if (!this.canDrain()) {
      return
    }

    const next = await db.outbox
      .where('[status+nextAttemptAt]')
      .between(['queued', 0], ['queued', Dexie.maxKey])
      .first()

    if (next === undefined || !this.canDrain()) {
      return
    }

    const delay = Math.max(0, next.nextAttemptAt - this.clock())
    this.clearRetryTimer()
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.wake()
    }, delay)
  }

  private canDrain(): boolean {
    return this.started && this.canDrainDirectly()
  }

  private canDrainDirectly(): boolean {
    return this.isOnline && this.isAuthenticated && !this.blockedBySession
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }
}

export function createSyncWorker(options: SyncWorkerOptions = {}): SyncWorker {
  return new OutboxSyncWorker(options)
}

/** Test and startup primitive for one synchronous drain pass without browser listeners. */
export function drainOutbox(options: SyncWorkerOptions = {}): Promise<void> {
  return new OutboxSyncWorker(options).drain()
}

async function dispatch(op: OutboxOp): Promise<BatchResult> {
  if (op.kind === 'assign') {
    const body: BatchAssignRequest = {
      palletCode: op.palletCode,
      serialNumbers: op.serialNumbers,
      overrideExisting: op.overrideExisting,
      idempotencyKey: op.id,
      capturedAt: op.capturedAt,
      captureMethod: op.captureMethod,
    }

    return palletApi.batchAssign(body)
  }

  if (op.kind === 'add') {
    const body: AddItemsRequest = {
      serialNumbers: op.serialNumbers,
      overrideExisting: op.overrideExisting,
      idempotencyKey: op.id,
      capturedAt: op.capturedAt,
      captureMethod: op.captureMethod,
    }

    return palletApi.addItems(op.palletCode, body)
  }

  const body: RemoveItemsRequest = {
    serialNumbers: op.serialNumbers,
    idempotencyKey: op.id,
    capturedAt: op.capturedAt,
    captureMethod: op.captureMethod,
  }

  return palletApi.removeItems(op.palletCode, body)
}

async function reconcile(op: OutboxOp, result: BatchResult, clock: () => number): Promise<void> {
  const hasRejectedSerial = result.results.some((item) => item.status === 'rejected')
  const status = hasRejectedSerial ? 'rejected' : 'done'
  const cached = await db.recentPallets.get(result.palletCode)

  await db.transaction('rw', db.outbox, db.recentPallets, db.basket, async () => {
    await db.outbox.update(op.id, {
      status,
      result,
      lastError: undefined,
    })

    if (op.kind === 'assign' && op.basketSeq !== undefined) {
      const serialNumber = op.serialNumbers[0]
      const basketEntry = serialNumber === undefined ? undefined : await db.basket.get(serialNumber)

      if (basketEntry?.seq === op.basketSeq) {
        await db.basket.delete(basketEntry.serialNumber)
      }
    }

    await db.recentPallets.put({
      palletCode: result.palletCode,
      itemCount: result.palletItemCount,
      capacityMax: result.palletCapacityMax,
      status: cached?.status ?? 'open',
      serialNumbers: cached?.serialNumbers ?? [],
      lastActivityAt: cached?.lastActivityAt ?? new Date(clock()).toISOString(),
      cachedAt: clock(),
    })
  })

  if (!hasRejectedSerial) {
    await recordSuccessfulSync(op.id, clock())
  }
}

function isSessionExpired(error: unknown): boolean {
  return error instanceof ApiError && error.isSessionExpired
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'network unreachable'
}
